import * as React from 'react'
import { FunctionComponent, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import data from './../fixtures/data.json'
import { PageNotFound, useValidateLanguage } from './pages'
import i18n from 'i18next'
import {
  FrontHeaderStyled,
  HiddenSpanStyled,
  ImageContainerStyled,
  MainPageContainerStyled,
  NavLinkStyled,
  PeakMeterAnimation,
  StationPageHeaderStyled,
  StationPageImageStyled,
  TocLinkStyled,
  TrackListContainerStyled,
  TrackListItemStyled,
  TrackListStyled
} from '../components/components'
import { publicFilesLastCacheToken } from '../App'
import ImageIconPrevious from '../assets/images/icons/icon-arrow-previous.svg'
import ImageIconNext from '../assets/images/icons/icon-arrow-next.svg'
import { AudioPlayer } from '../components/AudioPlayer'
import { useSwipeable } from 'react-swipeable'
import ImageDomkirche from '../assets/images/domkirche.svg'
import { AudioContext, AudioContextType } from '../audioContext'
import { trackAudio, trackPageInteraction, usePageTracking } from '../utils/tracking'
import { useCookieConsent } from '../utils/customHooks'
import { calculatePlayedTime, convertTimeRangeToArray, mergeTimeRange } from '../utils/utils'
import { set } from 'immer/dist/utils/common'

//https://refine.dev/blog/usecontext-and-react-context/

// Preload function
// function load(src) {
//   return new Promise((resolve, reject) => {
//     const image = new Image()
//     image.addEventListener('load', resolve)
//     image.addEventListener('error', reject)
//     image.src = src
//   })
// }
//
// const image = 'http://placekitten.com/200/300'
// load(image).then(() => {
//   body.style.backgroundImage = `url(${image})`
// })

export type StationSrc = {
  srcSmall: string;
  srcSmall2x: string;
  srcMedium: string;
  srcMedium2x: string;
  positionY: string;
}
export type Station = {
  id: number,
  src: Record<string, string>,
  img: StationSrc,
}

export const MainPage: FunctionComponent = () => {
  const { isUserExperienceCookiesAllowed } = useCookieConsent()

  // -- params
  const { id, lang } = useParams()
  // console.log('id=' + id)
  const stationsCopy = data.stations.slice()
  const stations = stationsCopy.map((station, index) => { // currently id is the same as order of stations, may change in future
    station['id'] = (index + 1)
    return ((station as any) as Station)
  })

  usePageTracking(isUserExperienceCookiesAllowed, 'MainPage - Station: ' + id, lang)

  // -- validate page

  const pageNotFound = useValidateLanguage(lang)
  if (pageNotFound) {
    return pageNotFound
  }
  if (!parseInt(id) || parseInt(id) > stations.length) {
    return <PageNotFound></PageNotFound>
  }

  const getNextStationId = (): string => {
    let nextStationId = 1
    const currNumber = currStationData?.id
    if (currStationData) {
      if (currNumber === stations.length) nextStationId = 1 // #codereview redundant, haben wir oben; außerdem: wollen wir das, auf 1 setzen?
      // Rosi: Hm ich würde es so lassen: die Funktion gibt einfach im Kreis die nächste Nummer zurück, wie nun tatsächlich die Logik ist (ob es im Kreis gehen darf oder nicht), ist der Funktion egal
      else nextStationId = currNumber + 1
    }
    // debugger
    return nextStationId.toString()
  }

  const getPreviousStationId = (): string => {
    let previousStationId = 1
    const currNumber = currStationData?.id
    if (currStationData) {
      if (currNumber === 1) previousStationId = stations.length
      else previousStationId = currNumber - 1
    }
    return previousStationId.toString()
  }

  const mergeIntoTotalPlayedTimeRanges = (index, timeRangesArr, totalPlayedTimeRanges) => {
    if (!timeRangesArr || !timeRangesArr.length) return totalPlayedTimeRanges
    const updatedTotalPlayedTimeRanges = [...totalPlayedTimeRanges]
    const mergedRanges = mergeTimeRange(totalPlayedTimeRanges[index], timeRangesArr)
    updatedTotalPlayedTimeRanges[index] = mergedRanges
    return updatedTotalPlayedTimeRanges
  }

  const findStation = (id: string) => {
    return stations.find((item) => (item.id === parseInt(id)))
  }

  const isCurrStationFirstItem = () => {
    return (currStationData?.id === 1)
  }
  const isCurrStationLastItem = () => {
    return (currStationData?.id === stations.length) // #CodeReview index statt id, und number? Do we really want to deal with strings?
  }

  const navigateToNext = () => {
    navigate(`/${i18n.resolvedLanguage}/station/${getNextStationId()}`, { replace: true })
  }

  const navigateToPrevious = () => {
    navigate(`/${i18n.resolvedLanguage}/station/${getPreviousStationId()}`, { replace: true })
  }

  const swipeHandlers = useSwipeable({
    onSwipedLeft: (eventData) => {
      if (!isCurrStationLastItem())
        navigateToNext()
    },
    onSwipedRight: (eventData) => {
      if (!isCurrStationFirstItem())
        navigateToPrevious()
    }
  })

  // -----------------------------------

  const { t } = useTranslation()
  const navigate = useNavigate()

  const {
    audio,
    setShouldPlayOnStart,
    shouldPlayOnStart,
    lastPlayHeadPosition,
    setLastPlayHeadPosition,
    audioDurations,
    setAudioDurations,
    setTotalPlayedTimeRanges,
    totalPlayedTimeRanges
  }
    = useContext(AudioContext) as AudioContextType

  // we know if we need to stop audio when user clicks on a station link in overview page. Otherwise if audio of station is nearly ended or ended,
  // the user could not jump this station. // TODO cleanup data structure, one data object for each station instead of these arrays!

  const [mode, setMode] = useState<'station' | 'overview'>('station')

  const currStationData = findStation(id)
  // console.log('currStationData=', currStationData, 'id=', id)
  const currIndex = parseInt(id)
  const displayedIndices = [currIndex - 1, currIndex, currIndex + 1]
  const scrollContainerRef = useRef(null)
  const overviewAnchorItemRef = useRef(null)

  const displayedStations = displayedIndices
    .map(index => (
      {
        stationData: findStation(index.toString()),
        offset: index - currIndex
      }
    ))
  // .sort((a, b) => a.key - b.key) // this is not necessary, react's keys will take care of that

  const audioSrc = currStationData?.src[lang]
  const initialLastPLayHeadPosition = lastPlayHeadPosition[parseInt(id) - 1]

  useEffect(() => {
    return () => {
      const audioDuration = audio.duration
      const audioPlayed = audio.played
      // TODO sumPlayed is wrong!
      // console.log('*** return called', audio.currentTime, audio.ended, audio.src)
      const minCurrentTime = 15 //m min time the audio must be watched, before we save it
      trackAudio(isUserExperienceCookiesAllowed, 'lastPlayPositionOnStationExit', id, true, Math.round(audio.currentTime / audio.duration * 100))

      setTotalPlayedTimeRanges((value) => {
        const newTotalPlayedTimeRanges = mergeIntoTotalPlayedTimeRanges(parseInt(id) - 1, convertTimeRangeToArray(audioPlayed), value)
        // console.log('get totalPlayedTimeRanges: ', calculatePlayedTime(newTotalPlayedTimeRanges[parseInt(id) -1], ', get duration: ', audio.duration))
        const playedPercentage = Math.round(calculatePlayedTime(newTotalPlayedTimeRanges[parseInt(id) - 1], audioDuration))
        trackAudio(isUserExperienceCookiesAllowed, 'sumPlayedOnStationExit', id, true, playedPercentage)
        trackAudio(isUserExperienceCookiesAllowed, 'sumPlayedOnStationExitFlooredToFifth', id, true, Math.floor(playedPercentage / 20) * 20)
        return newTotalPlayedTimeRanges
      })

      if (audio.currentTime > minCurrentTime) {
        const playHeadPosition = (!audio.ended) ? audio.currentTime : audio.duration
        setLastPlayHeadPosition((value) => {
          const updatedPlayHeadList = [...value]
          updatedPlayHeadList[parseInt(id) - 1] = playHeadPosition
          return updatedPlayHeadList
        })
      }
      if (audio.duration > 0 && isFinite(audio.duration)) { // may be infinite on iphone
        setAudioDurations((value) => {
          const updatedAudioDurations = [...value]
          updatedAudioDurations[parseInt(id) - 1] = audioDuration
          return updatedAudioDurations
        })
      }
    }
  }, [id, lang])

  const onTrackEnded = useCallback((isPlaying) => {
    if (isPlaying) {
      setShouldPlayOnStart(false)
      if (!isCurrStationLastItem()) {
        navigateToNext()
      }
      trackAudio(isUserExperienceCookiesAllowed, 'audioEnded', id, true)
    }
  }, [id])

  const scrollToAnchorItem = useCallback(node => {
    if (node !== null && overviewAnchorItemRef.current) {
      overviewAnchorItemRef.current.scrollIntoView({ //versus window.scrollTo
        // top: overviewAnchorItemRef.current.getBoundingClientRect().top
        // behavior: 'smooth',
        inline: 'start',
        block: 'center'
      })
    }
  }, [])

  return (
    <>
      <TocLinkStyled onClick={() => {
        mode === 'overview' ? (setMode('station')) : setMode('overview')
        trackPageInteraction(isUserExperienceCookiesAllowed, mode === 'overview' ? 'hideOverviewClick' : 'showOverviewClick', 'station: ' + id, parseInt(id))
      }}><HiddenSpanStyled>Overview</HiddenSpanStyled></TocLinkStyled>
      {(mode === 'overview') && (
        <>
          <MainPageContainerStyled>
            <TrackListContainerStyled ref={scrollToAnchorItem}>
              <FrontHeaderStyled small={true}>
                <Link to={`/${i18n.resolvedLanguage}`}>
                  <div>
                    <img src={ImageDomkirche} width="102" height="163" alt="Domkirche St. Stephan" />
                    <h1>
                      <span>AUDIOGUIDE</span>
                      <span>Domkirche</span>
                      <span>St.&#160;Stephan</span>
                    </h1>
                  </div>
                </Link>
              </FrontHeaderStyled>
              <TrackListStyled>
                {stations.map((track, i) => {

                  return (<TrackListItemStyled key={track.id} ref={currStationData.id === track.id ? overviewAnchorItemRef : null}
                                               state={currStationData.id === track.id ? 'active' : (track.id < currStationData.id ? 'past' : '')}>
                    <Link to={`/${i18n.resolvedLanguage}/station/${i + 1}`} onClick={() => {
                      setMode('station')
                      setShouldPlayOnStart((audioDurations[i] === 0 || (lastPlayHeadPosition[i] + 15) <= audioDurations[i]))
                      trackPageInteraction(isUserExperienceCookiesAllowed, 'stationLinkInListClick', 'station: ' + id, parseInt(id))
                    }}><span>{i + 1}</span><span dangerouslySetInnerHTML={{ __html: t(`tracks.${track.id}`) }}></span>{currStationData.id === track.id ? <PeakMeterAnimation></PeakMeterAnimation> : ''}
                    </Link>
                  </TrackListItemStyled>)
                })}
              </TrackListStyled>
            </TrackListContainerStyled>
          </MainPageContainerStyled>
        </>
      )
      }
      {
        (mode === 'station') && (
          <MainPageContainerStyled {...swipeHandlers}>
            <StationPageHeaderStyled>
              <h1>
                <span>{currStationData.id}</span>
                <span dangerouslySetInnerHTML={{ __html: t(`tracks.${currStationData.id}`) }}></span>
              </h1>
            </StationPageHeaderStyled>

            <ImageContainerStyled>
              {displayedStations.map(({ stationData, offset }) => (
                stationData &&
                <StationPageImageStyled
                  key={stationData.id || '666'}
                  offset={offset}
                  srcSmall={stationData.img.srcSmall + '?c=' + publicFilesLastCacheToken} srcSmall2x={stationData.img.srcSmall2x + '?c=' + publicFilesLastCacheToken}
                  srcMedium={stationData.img.srcMedium + '?c=' + publicFilesLastCacheToken} srcMedium2x={stationData.img.srcMedium2x + '?c=' + publicFilesLastCacheToken}
                  srcLarge={stationData.img.srcMedium + '?c=' + publicFilesLastCacheToken} srcLarge2x={stationData.img.srcMedium2x + '?c=' + publicFilesLastCacheToken}
                  positionY={stationData.img.positionY}
                />
              ))
              }
            </ImageContainerStyled>

            <NavLinkStyled type={'previous'} to={`/${i18n.resolvedLanguage}/station/${getPreviousStationId()}`}
                           onClick={() => {
                             setShouldPlayOnStart(false)
                             trackPageInteraction(isUserExperienceCookiesAllowed, 'previousStationLinkClick', 'station: ' + id, parseInt(id))
                           }} disabled={isCurrStationFirstItem()}><img src={ImageIconPrevious} alt="previous"
                                                                       width="32px" height="32px" /></NavLinkStyled>
            <NavLinkStyled type={'next'} to={`/${i18n.resolvedLanguage}/station/${getNextStationId()}`}
                           onClick={() => {
                             setShouldPlayOnStart(false)
                             trackPageInteraction(isUserExperienceCookiesAllowed, 'nextStationLinkClick', 'station: ' + id, parseInt(id))
                           }} disabled={isCurrStationLastItem()}><img src={ImageIconNext} alt="next" width="32px"
                                                                      height="32px" /></NavLinkStyled>
          </MainPageContainerStyled>
        )
      }
      <AudioPlayer
        isSticky={false}
        src={audioSrc + '?c=' + publicFilesLastCacheToken}
        audio={audio}
        shouldPlayOnStart={shouldPlayOnStart}
        onPreviousClick={() => {

          setShouldPlayOnStart(false)
          trackAudio(isUserExperienceCookiesAllowed, 'previousClick', id, false)
          if (audio.currentTime > 0 || isCurrStationFirstItem()) {
            audio.currentTime = 0
          } else {
            navigateToPrevious()
          }
        }}
        onNextClick={() => {
          setShouldPlayOnStart(false)
          trackAudio(isUserExperienceCookiesAllowed, 'nextClick', id, false)
          navigateToNext()
        }}
        initialCurrentTime={initialLastPLayHeadPosition}
        onEnded={(isPlaying) => {
          if (isPlaying) {
            setShouldPlayOnStart(false)
            if (!isCurrStationLastItem()) {
              navigateToNext()
            } // else we stop audio (is already done, cause we always stop audio onEnded)
            trackAudio(isUserExperienceCookiesAllowed, 'audioEnded', id, true)
          }
        }}
        btnNextDisabled={isCurrStationLastItem()} btnPreviousDisabled={isCurrStationFirstItem()}></AudioPlayer>
    </>
  )

}
